import React from 'react';
import { TabsHeader, TabsPanel } from '@schamburg-web/components';
import EmailIcon from '@mui/icons-material/Email';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';
import { Box, Tab, Button, useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ManageAccount from '../ManageAccount/ManageAccount';
import ModifyAccountStepper from '../ModifyAccountStepper/ModifyAccountStepper';
import { useLoginContext } from '../../../contexts/LoginContext';

const tabs: {
  name: string;
  component?: React.ComponentType;
  icon: React.ReactElement;
}[] = [
  {
    name: 'Manage',
    component: ManageAccount,
    icon: <ManageAccountsIcon />,
  },
  {
    name: 'Edit',
    component: ModifyAccountStepper,
    icon: <EmailIcon />,
  },
];

const AccountPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useLoginContext();
  const value = Number(searchParams.get('tab') || 0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSearchParams((params) => {
      params.set('tab', newValue.toString());
      return params;
    });
  };

  // Responsive: use MUI's useMediaQuery for mobile detection
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'center',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 1 : 2,
          paddingTop: isMobile ? '0.5em' : '1em',
          px: isMobile ? 1 : 0,
        }}
      >
        <Box
          sx={{
            display: isMobile ? 'flex' : 'block',
            alignItems: 'center',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <TabsHeader
            value={value}
            onChange={handleTabChange}
            sx={{
              textAlign: 'center',
              width: isMobile ? '100%' : 'auto',
              '.MuiButtonBase-root': {
                fontSize: isMobile ? '15px' : '16px',
                textTransform: 'none',
                maxHeight: 'initial !important',
                minHeight: 'initial !important',
                marginRight: isMobile ? '0.5em' : '1em',
                marginLeft: isMobile ? '0.5em' : '1em',
              },
            }}
          >
            {tabs.map((t) => (
              <Tab
                icon={t.icon}
                label={t.name}
                iconPosition="start"
                key={t.name}
              />
            ))}
          </TabsHeader>
          {isMobile && <LogoutButton />}
        </Box>

        {/* Username display */}
        {!isMobile && (
          <Box
            sx={{
              minWidth: 0,
              flexShrink: 0,
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 0.5,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                borderRadius: 2,
                fontSize: isMobile ? '0.9rem' : '0.95rem',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                marginTop: isMobile ? 1 : 0,
                marginBottom: isMobile ? 1 : 0,
                justifyContent: isMobile ? 'center' : 'flex-start',
              }}
            >
              {user?.username ? `Signed in as: ${user.username}` : ''}
            </Box>
          </Box>
        )}

        {!isMobile && <LogoutButton />}
      </Box>

      {tabs.map((t, index) => {
        const Component = t.component;
        return (
          <TabsPanel index={value} value={index} key={t.name}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              {Component && React.isValidElement(<Component />) ? (
                <Component />
              ) : null}
            </Box>
          </TabsPanel>
        );
      })}
    </Box>
  );
};

const LogoutButton: React.FC = () => {
  const { logout } = useLoginContext();

  // Responsive: use MUI's useMediaQuery for mobile detection
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Button
      variant="outlined"
      startIcon={<LogoutIcon />}
      onClick={logout}
      sx={
        isMobile
          ? {
              textTransform: 'none',
              minWidth: 'auto',
              marginLeft: 1,
              alignSelf: 'center',
            }
          : {
              textTransform: 'none',
              marginTop: 0,
            }
      }
    >
      Logout
    </Button>
  );
};

export default AccountPage;
